/**
 * WebSocket Service
 * 
 * Provides real-time updates via Socket.IO
 */

import { io, Socket } from 'socket.io-client';
import { getStoredAccessToken } from './authStorage';
import { ensureUiScope } from './api';

// Default to same-origin; nginx proxies /socket.io/* to cloud_api.
const WS_URL =
  process.env.REACT_APP_WS_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000');

export type EventType = 
  | 'alert.new'
  | 'alert.resolved'
  | 'detection.new'
  | 'device.status'
  | 'zone.update'
  | 'metric.update'
  | 'safety.event';

export interface WebSocketEvent {
  type: string;
  topic?: string;
  timestamp: string;
  data: Record<string, any>;
  device_id?: string;
}

export type EventHandler = (event: WebSocketEvent) => void;

class WebSocketService {
  private socket: Socket | null = null;
  private handlers: Map<string, Set<EventHandler>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private connected = false;
  private connectionHandlers: Set<(connected: boolean) => void> = new Set();

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    if (this.socket?.connected) {
      console.log('WebSocket already connected');
      return;
    }

    console.log('Connecting to WebSocket:', WS_URL);
    const token =
      getStoredAccessToken() || '';
    const scope = (() => {
      try {
        const s = ensureUiScope();
        if (s.orgId && s.siteId) return { org_id: s.orgId, site_id: s.siteId };
      } catch {
        // ignore
      }
      try {
        const sp = new URLSearchParams(typeof window !== 'undefined' ? (window.location?.search || '') : '');
        const site = (sp.get('site') || sp.get('site_id') || '').trim();
        const org = (sp.get('org') || sp.get('org_id') || '').trim();
        if (org && site) return { org_id: org, site_id: site };
      } catch {
        // ignore
      }
      return null;
    })();
    
    this.socket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      // Backend (prod/strict) requires JWT at connect time.
      auth: token ? { token, ...(scope || {}) } : undefined,
    });

    this.setupEventHandlers();
  }

  /**
   * Setup core event handlers
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('WebSocket connected');
      this.connected = true;
      this.reconnectAttempts = 0;
      
      // Subscribe to all events by default
      this.socket?.emit('subscribe', { room: 'all' });
      this.connectionHandlers.forEach((handler) => {
        try {
          handler(true);
        } catch (error) {
          console.error('Error in connection handler:', error);
        }
      });
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      this.connected = false;
      this.connectionHandlers.forEach((handler) => {
        try {
          handler(false);
        } catch (error) {
          console.error('Error in connection handler:', error);
        }
      });
    });

    this.socket.on('connect_error', () => {
      // Expected when cloud_api runs with gthread workers (default Docker).
      // To enable Socket.IO: set GUNICORN_USE_EVENTLET=true and use eventlet worker.
      this.reconnectAttempts++;
    });

    this.socket.on('connected', (data) => {
      console.log('WebSocket welcome:', data);
    });

    // Setup event forwarding for all event types
    const eventTypes: EventType[] = [
      'alert.new',
      'alert.resolved',
      'detection.new',
      'device.status',
      'zone.update',
      'metric.update',
      'safety.event',
    ];

    eventTypes.forEach((eventType) => {
      this.socket?.on(eventType, (event: WebSocketEvent) => {
        this.dispatchEvent(eventType, event);
      });
    });

    // Handle pong for keepalive
    this.socket.on('pong', (data) => {
      console.debug('WebSocket pong:', data);
    });
  }

  /**
   * Dispatch event to registered handlers
   */
  private dispatchEvent(eventType: string, event: WebSocketEvent): void {
    // Dispatch to specific handlers
    const handlers = this.handlers.get(eventType);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(event);
        } catch (error) {
          console.error(`Error in ${eventType} handler:`, error);
        }
      });
    }

    // Dispatch to wildcard handlers
    const wildcardHandlers = this.handlers.get('*');
    if (wildcardHandlers) {
      wildcardHandlers.forEach((handler) => {
        try {
          handler({ ...event, type: eventType });
        } catch (error) {
          console.error('Error in wildcard handler:', error);
        }
      });
    }
  }

  /**
   * Subscribe to events
   */
  on(eventType: EventType | '*', handler: EventHandler): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());
    }
    this.handlers.get(eventType)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.handlers.get(eventType)?.delete(handler);
    };
  }

  /**
   * Unsubscribe from events
   */
  off(eventType: EventType | '*', handler: EventHandler): void {
    this.handlers.get(eventType)?.delete(handler);
  }

  /**
   * Subscribe to a specific room (zone, device, etc.)
   */
  subscribeToRoom(room: string): void {
    this.socket?.emit('subscribe', { room });
  }

  /**
   * Unsubscribe from a room
   */
  unsubscribeFromRoom(room: string): void {
    this.socket?.emit('unsubscribe', { room });
  }

  /**
   * Acknowledge an alert
   */
  acknowledgeAlert(alertId: string, deviceId: string): void {
    this.socket?.emit('ack_alert', { alert_id: alertId, device_id: deviceId });
  }

  /**
   * Send ping for keepalive
   */
  ping(): void {
    this.socket?.emit('ping');
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.connected = false;
  }

  /**
   * Subscribe to connect/disconnect, including reconnect.
   */
  onConnectionChange(handler: (connected: boolean) => void): () => void {
    this.connectionHandlers.add(handler);
    return () => {
      this.connectionHandlers.delete(handler);
    };
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connected && (this.socket?.connected ?? false);
  }

  /**
   * Get connection status
   */
  getStatus(): { connected: boolean; reconnectAttempts: number } {
    return {
      connected: this.isConnected(),
      reconnectAttempts: this.reconnectAttempts,
    };
  }
}

// Singleton instance
export const websocketService = new WebSocketService();

export default websocketService;
